import { Field } from '@app/Tenant/Applications/Airsweb/Models';
import { LocationAttribute } from '@app/Tenant/Applications/Airsweb/Models/location-attribute.model';

export interface FieldAliasSources {
    fields: Iterable<Field>;
    hierarchyAttributes: Iterable<LocationAttribute>;
}

/**
 * Returns the original-cased ParentPropertyName from `parsedJson` when it
 * collides with another field's FieldId or with any HA's AttributeId/AttributeName
 * (case-insensitive). Returns null when there's no conflict, the input is missing
 * a string ParentPropertyName, or `parsedJson` isn't an object.
 *
 * `currentField` is excluded from the scan by reference identity so editing a
 * field whose own FieldId matches the JSON's ParentPropertyName isn't a false hit.
 */
export function findFieldAliasConflict(
    parsedJson: unknown,
    currentField: Field | null,
    sources: FieldAliasSources
): string | null {
    const parentPropertyName = readParentPropertyName(parsedJson);
    if (parentPropertyName === null) {
        return null;
    }

    const target = parentPropertyName.toUpperCase();

    const isUnchangedSelfAlias = currentField?.FieldId?.toUpperCase() === target;
    const fieldConflict = !isUnchangedSelfAlias && matchesField(target, sources.fields, currentField);

    if (fieldConflict || matchesHa(target, sources.hierarchyAttributes)) {
        return parentPropertyName;
    }
    return null;
}

function readParentPropertyName(parsedJson: unknown): string | null {
    if (!parsedJson || typeof parsedJson !== 'object') {
        return null;
    }
    const value = (parsedJson as { ParentPropertyName?: unknown }).ParentPropertyName;
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function matchesField(target: string, fields: Iterable<Field>, currentField: Field | null): boolean {
    for (const f of fields) {
        if (f === currentField) {
            continue;
        }
        if (f?.FieldId?.toUpperCase() === target) {
            return true;
        }
    }
    return false;
}

function matchesHa(target: string, has: Iterable<LocationAttribute>): boolean {
    for (const ha of has) {
        if (ha?.AttributeId?.toUpperCase() === target || ha?.AttributeName?.toUpperCase() === target) {
            return true;
        }
    }
    return false;
}
